/** 0 -> 1: wrap a bare state dump in a save envelope.
 *
 * Format 0 is not a format anyone designed; it is what existed before this one.
 * The headless harness and the debug console both wrote `JSON.stringify(state)`
 * straight out, and those blobs are sitting in browser storage and in bug
 * reports. They are a whole, valid `GameState` with no envelope around them, so
 * lifting one costs a wrapper and nothing else.
 *
 * The template for the next one: read only what you need out of `raw`, write a
 * new object, never mutate the input, and never reach for `GameState` - by the
 * time 0002 exists this file describes a shape the type no longer has.
 */

import type { Migration } from "./types.js";
import { isRecord, stringAt } from "./tree.js";

export const migration0001: Migration = {
  from: 0,
  to: 1,
  describe: "wrap a bare GameState dump in the realm.save envelope",
  migrate(raw: unknown): Record<string, unknown> | null {
    if (!isRecord(raw)) return null;

    // Already enveloped, just unstamped: an early build wrote the wrapper
    // without a `kind`. Take its state and re-stamp it.
    const inner = isRecord(raw["state"]) ? raw["state"] : raw;

    const contentVersion = stringAt(inner, "contentVersion")
      ?? stringAt(raw, "contentVersion") ?? "";

    return {
      kind: "realm.save",
      formatVersion: 1,
      contentVersion,
      state: { ...inner, formatVersion: 1, contentVersion },
    };
  },
};
